import { ContentItem } from 'models/ui';

export enum FormField {
  PATIENT_LEGAL_GUARDIAN = 'patientLegalGuardian',
  PATIENT_NAME = 'patientName',
  DECISION = 'decision',
  PATIENT_GUARDIAN_SIGNATURE = 'patientGuardianSignature',
  PATIENT_GUARDIAN_DAY = 'patientGuardianDay',
  PROVIDER_DESIGNEE_SIGNATURE = 'providerDesigneeSignature',
  PROVIDER_DESIGNEE_DAY = 'providerDesigneeDay',
  WITNESS_SIGNATURE = 'witnessSignature',
  WITNESS_DAY = 'witnessDay',
  PROVIDER_NURSE = 'providerNurse'
}

export enum FormFieldLabel {
  PATIENT_LEGAL_GUARDIAN = 'Patient / Legal Guardian',
  PATIENT_NAME = "Myself/Patient's Name",
  DECISION = 'Decision',
  PATIENT_GUARDIAN_SIGNATURE = 'Patient / Guardian Signature',
  PROVIDER_DESIGNEE_SIGNATURE = 'Provider / Designee Signature',
  WITNESS_SIGNATURE = 'Witness Signature',
  PROVIDER_NURSE = 'Provider / Nurse'
}

/** All three "Select Day" fields share one label, which doubles as the select's placeholder. */
export const SELECT_DAY_LABEL = 'Select Day';

export enum Placeholder {
  PATIENT_LEGAL_GUARDIAN = 'Patient / Legal Guardian',
  PATIENT_NAME = "Myself / Patient's Name"
}

export enum DropdownAction {
  CLEAR_LABEL = 'Clear',
  CLEAR_OPTION_ID = '__clear__'
}

export const PLACEHOLDER_OPTION_ID = '__placeholder__';

export const buildPlaceholderItem = (placeholder: string): ContentItem =>
  ({
    id: PLACEHOLDER_OPTION_ID,
    name: placeholder,
    label: placeholder,
    value: '',
    selected: false,
    isActive: true,
    isHidden: true,
    hidden: true,
    disabled: true,
    displayOrder: -1,
    onChange: () => undefined
  } as unknown as ContentItem);

/** Fills the blank slot the backend metadata leaves at the top of the list. */
export const buildClearItem = (): ContentItem =>
  ({
    id: DropdownAction.CLEAR_OPTION_ID,
    name: DropdownAction.CLEAR_LABEL,
    label: DropdownAction.CLEAR_LABEL,
    value: '',
    selected: false,
    isActive: true,
    isHidden: false,
    displayOrder: 0,
    onChange: () => undefined
  } as unknown as ContentItem);

export enum DecisionValue {
  CONSENT = 'consent',
  REFUSE = 'refuse'
}

export enum SectionTitle {
  CONSENT_FOR_BLOOD_TRANSFUSION = 'Consent for Blood Transfusion',
  CONSENT_FOR_BLOOD_TRANSFUSION_FORM = 'Consent for Blood Transfusion or Blood Components',
  GENERAL_INFORMATION = 'General Information'
}

export const CONSENT_PARAGRAPHS = [
  'The benefits and risks of blood use, as well as the risks of refusal and potential alternatives, have been explained.',
  'Potential benefits of receiving blood or blood components include restoration of blood volume, replacement of clotting factors, and improved oxygen carrying capacity.',
  'Although blood is screened for transmittable diseases, risks of blood or blood components include, but are not limited to blood-borne infections and transfusion reactions such as headache, fever, chills, rash, difficulty breathing, and excessive blood volume.',
  'Risks of not receiving blood or blood components include bleeding, stroke, heart attack, and death. Potential alternatives to receiving blood or blood components that may be available are self-donation, directed donations, erythropoietin therapy, and cell-saving technologies.'
];

export const CONSENT_SENTENCE = {
  LEAD_IN: 'I',
  AUTHORIZE: 'hereby authorize and consent to the administration of blood and/or blood components for',
  FOLLOW_UP: 'now and/or during the course of therapy, surgery, or illness as deemed advisable by responsible physicians.',
  DISCUSSION: 'I have been given the opportunity to discuss the risks and benefits of transfusion therapy and I:',
  USE_OF_BLOOD: 'to the use of blood and/or blood components now and/or during the course of treatment.'
};

export const NON_REQUIRED_FIELDS: string[] = [
  FormField.PATIENT_LEGAL_GUARDIAN,
  FormField.PATIENT_NAME,
  FormField.DECISION,
  FormField.PATIENT_GUARDIAN_SIGNATURE,
  FormField.PATIENT_GUARDIAN_DAY,
  FormField.PROVIDER_DESIGNEE_SIGNATURE,
  FormField.PROVIDER_DESIGNEE_DAY,
  FormField.WITNESS_SIGNATURE,
  FormField.WITNESS_DAY
];

export const SIGNATURE_FIELDS: FormField[] = [
  FormField.PATIENT_GUARDIAN_SIGNATURE,
  FormField.PATIENT_GUARDIAN_DAY,
  FormField.PROVIDER_DESIGNEE_SIGNATURE,
  FormField.PROVIDER_DESIGNEE_DAY,
  FormField.WITNESS_SIGNATURE,
  FormField.WITNESS_DAY
];

export const DAY_FIELDS: FormField[] = [FormField.PATIENT_GUARDIAN_DAY, FormField.PROVIDER_DESIGNEE_DAY, FormField.WITNESS_DAY];

export const REQUIRED_SIGNATURE_FIELDS: FormField[] = [FormField.PATIENT_GUARDIAN_SIGNATURE, FormField.PROVIDER_DESIGNEE_SIGNATURE, FormField.WITNESS_SIGNATURE];
